<template lang="pug">
    button(
        ref="_in",
        type="button",
        class="_btn",
        :class="_class",
        :style="{ width, height }",
        :id="id",
        :disabled="disabled || busy",
        :inert="inert",
        :tabindex="inert ? -1 : tabindex",
        v-on="__on")
        slot &zwnj;
        span(v-if="busy", class="_busyi")
            slot(name="busy")
                ib-progress-dots
</template>

<script>
    import {
        bindKey,
        unbindKey
    } from '../../js/window'

    import entry from '../form/ib-form-entry.vue';

    function noop() { }


    /**
     * A stylized `<button>` control.
     */
    export default {
        name: 'ib-button',

        mixins: [
            entry
        ],

        props: {
            /**
             * Semantic color class.
             */
            tone: String,
            /**
             * Appearance modifier: `outline`, `clear`, `link`, `pill`, `fab`, and `flat` are supported.
             */
            appearance: String,
            /**
             * If true, display the busy placeholder and act as disabled.
             */
            busy: Boolean,
            /**
             * If true, take focus when loaded or made visible.
             */
            autofocus: Boolean,
            /**
             * Key or key combination to bind to this element.
             */
            bind: String
        },

        data() {
            return {
                bound: null
            };
        },

        computed: {
            _class() {
                const tone = this.tone;
                const appearance = this.appearance;
                return {
                    [tone]: tone,
                    [appearance]: appearance,
                    _busy: this.busy
                };
            }
        },

        watch: {
            bind(bind) {
                this.bindKey(bind);
            }
        },

        mounted() {
            this.bindKey(this.bind);
            if (this.$device.ios) {
                this.$el.addEventListener('touchstart', noop, true);
            }
        },

        destroyed() {
            this.bindKey(null);
        },

        methods: {
            /** Can be called externally to simulate a click */
            click() {
                this.$emit('click');
            },

            bindKey(bind) {
                if (this.bound) {
                    unbindKey(this.bound, this.$el);
                }
                if (bind) {
                    bindKey(bind, this.$el);
                    this.bound = bind;
                }
            }
        }
    }
</script>

<style lang="less">
    @import "../../less/globals.less";

    .btn-solid(@fill, @stroke) {
        color: @stroke;
        background-color: @fill;
    }
    .btn-outline(@fill, @stroke) {
        color: @stroke;
        border-color: @fill;
    }

    @keyframes _btn-busy {
        0% {
            opacity: 0;
        } 100% {
            opacity: 1;
        }
    }

    ._btn {
        position: relative;
        margin: 2px 4px;
        padding: @btn-padding;
        border-radius: @btn-radius;
        color: @fg-button;
        background-color: @bg-button;
        font-family: @font-family;
        .font-size(@fs6);
        text-align: center;
        text-overflow: ellipsis;
        white-space: nowrap;
        cursor: pointer;
        z-index: 0;
        -webkit-tap-highlight-color: transparent;
        & when not (@btn-border-size = 0) {
            -webkit-background-clip: border-box;
            background-clip: border-box;
        }

        /* hover overlay */
        &::after {
            content: '';
            position: absolute;
            top: -@btn-border-size;
            right: -@btn-border-size;
            bottom: -@btn-border-size;
            left: -@btn-border-size;
            opacity: 0;
            border-radius: inherit;
            pointer-events: none;
        }

        &::-moz-focus-inner {
            padding: 0;
            border: 0;
        }
        &:invalid {
            outline: none;
        }
        &.@{btn-focus},
        &.@{btn-hover},
        &:@{btn-focus},
        &:@{btn-hover} {
            &::after {
                opacity: @btn-focus-opacity;
            }
        }
        &:disabled {
            cursor: default;
            opacity: .3;
        }
        .btn-default();
    }

    ._btn.flat,
    ._btn-seg ._btn {
        .btn-flat();
    }

    ._btn.outline {
        .btn-outline();
    }

    ._btn {
        &.xs {
            .font-size(@fs8);
            padding: @btn-padding-xs;
        }
        &.sm {
            .font-size(@fs7);
            padding: @btn-padding-sm;
        }
        &.lg {
            padding: @btn-padding-lg;
        }
        &.xl {
            padding: @btn-padding-xl;
        }

        &.accent    { .btn-solid(@bg-accent, #fff); }
        &.positive  { .btn-solid(@bg-positive, #fff); }
        &.ok        { .btn-solid(@bg-ok, #fff); }
        &.add       { .btn-solid(@bg-add, #fff); }
        &.negative  { .btn-solid(@bg-negative, #fff); }
        &.sell      { .btn-solid(@bg-sell, #fff); }
        &.error     { .btn-solid(@bg-error, #fff); }
        &.remove    { .btn-solid(@bg-remove, #fff); }
        &.alert     { .btn-solid(@bg-alert, #fff); }
        &.buy       { .btn-solid(@bg-buy, #fff); }
        &.hold      { .btn-solid(@bg-hold, #fff); }
        &.warning   { .btn-solid(@bg-warning, #000); }
        &.important { .btn-solid(@bg-important, #000); }
        &.outline,
        &.clear     {
            color: @fg-gray;
        }
        &.link      {
            color: @fg-accent;
        }
        &.outline,
        &.clear,
        &.link {
            &.accent    { .btn-outline(@bg-accent, @fg-accent); }
            &.positive  { .btn-outline(@bg-positive, @fg-positive); }
            &.ok        { .btn-outline(@bg-ok, @fg-ok); }
            &.add       { .btn-outline(@bg-add, @fg-add); }
            &.negative  { .btn-outline(@bg-negative, @fg-negative); }
            &.sell      { .btn-outline(@bg-sell, @fg-sell); }
            &.error     { .btn-outline(@bg-error, @fg-error); }
            &.remove    { .btn-outline(@bg-remove, @fg-remove); }
            &.alert     { .btn-outline(@bg-alert, @fg-alert); }
            &.buy       { .btn-outline(@bg-buy, @fg-buy); }
            &.hold      { .btn-outline(@bg-hold, @fg-hold); }
            &.ok        { .btn-outline(@bg-ok, @fg-ok); }
            &.warning   { .btn-outline(@bg-warning, @fg-warning); }
            &.important { .btn-outline(@bg-important, @fg-important); }
        }

        /** No background */
        &.outline {
            background: none;
            & when (iscolor(@fg-button-outline)) {
                color: @fg-button-outline;
            }
            & when (iscolor(@border-button-outline)) {
                border-color: @border-button-outline;
            }
        }
        &.clear,
        &.link {
            border: unset;
            background-color: unset;
            & when not (@btn-border-size = 0) {
                &::after {
                    top: 0;
                    right: 0;
                    bottom: 0;
                    left: 0;
                }
            }
        }

        /** No button profile or background; useful for links/icons */
        &.link {
            max-width: none;
            height: auto;
            line-height: inherit;
            margin: 0;
            padding: 0;

            &.@{btn-focus},
            &.@{btn-hover},
            &:@{btn-focus},
            &:@{btn-hover} {
                &:not(:disabled) {
                    & {
                      color: mix(@fg-max, @fg-accent, 50%);
                    }
                    &.fg,
                    &.fg-heading {
                      color: @fg-accent;
                    }
                    &.fg-positive   { color: mix(@fg-max, @fg-positive, 50%); }
                    &.fg-ok         { color: mix(@fg-max, @fg-ok, 50%); }
                    &.fg-add        { color: mix(@fg-max, @fg-add, 50%); }
                    &.fg-negative   { color: mix(@fg-max, @fg-negative, 50%); }
                    &.fg-sell       { color: mix(@fg-max, @fg-sell, 50%); }
                    &.fg-error      { color: mix(@fg-max, @fg-error, 50%); }
                    &.fg-remove     { color: mix(@fg-max, @fg-remove, 50%); }
                    &.fg-alert      { color: mix(@fg-max, @fg-alert, 50%); }
                    &.fg-buy        { color: mix(@fg-max, @fg-buy, 50%); }
                    &.fg-hold       { color: mix(@fg-max, @fg-hold, 50%); }
                    &.fg-ok         { color: mix(@fg-max, @fg-ok, 50%); }
                    &.fg-warning    { color: mix(@fg-max, @fg-warning, 50%); }
                    &.fg-important  { color: mix(@fg-max, @fg-important, 50%); }
                    svg {
                        .svg-hover();
                    }
                }
            }
            &::after {
                content: none !important;
            }
        }

        /** Reduced left and right padding */
        &.narrow {
            padding-left: 8px;
            padding-right: 8px;
        }

        /** Pill button - rounded ends */
        &.pill {
            border-radius: 99em;
        }

        /** Floating action button - round */
        &.fab {
            width: 2.25em;
            height: 2.25em;
            max-width: none;
            margin: 0;
            padding: 0;
            border-radius: 50%;
            line-height: 0;
            &.xs {
                width: 1.75em;
                height: 1.75em;
            }
            &.sm {
                width: 2em;
                height: 2em;
            }
            &.lg {
                width: 2.5em;
                height: 2.5em;
            }
            &.xl {
                width: 2.75em;
                height: 2.75em;
            }
        }

        /** Busy state */
        &._busy {
            &::after {
                z-index: 0;
            }
        }
        > ._busyi {
            position: absolute;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            background: inherit;
            border-radius: inherit;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            animation: _btn-busy .4s;

            > ._dots {
                width: 50%;
                height: 50%;
                min-width: 2em;
                max-width: 3em;
            }
        }

        /** Badge position */
        > ._badge {
            top: -7px;
            right: 4px;
            z-index: 3;
        }
    }

    /** Traditional Chinese positive/negative colors */

    ._trcc ._btn {
        &.positive  { .btn-solid(@bg-negative, #fff); }
        &.negative  { .btn-solid(@bg-positive, #fff); }
        &.outline,
        &.clear,
        &.link {
            &.positive  { .btn-outline(@bg-negative, @fg-negative); }
            &.negative  { .btn-outline(@bg-positive, @fg-positive); }
        }
        &.outline {
            background-color: @bg;
        }
        &.clear,
        &.link {
            background-color: transparent;
        }
    }
</style>

<ib-example id="tone" name="Tone">
    <template lang="pug">
        div
            div(class="after-8 fs7") Default
            div
                ib-button(
                    v-for="tone in [null,'accent','add','remove','buy','hold','sell','important','alert']",
                    :key="tone",
                    :tone="tone",
                    class="outset-4") {{tone || 'Default'}}
            div(class="before-16 after-8 fs7") appearance="outline"
            div
                ib-button(
                    v-for="tone in [null,'accent','add','remove','buy','hold','sell','important','alert']",
                    :key="tone",
                    :tone="tone",
                    class="outset-4",
                    appearance="outline") {{tone || 'Default'}}
    </template>
</ib-example>

<ib-example id="size" name="Size">
    <template lang="pug">
        div
            ib-button(v-for="size in ['xs','sm',null,'lg','xl']", :key="size", :class="size") {{size || 'Default'}}
    </template>
</ib-example>

<ib-example id="icon" name="Icons">
    <template lang="pug">
        include ../../icon/basic
        div
            ib-button
                +icon-basic-checkmark
            ib-button(class="link")
                +icon-basic-checkmark
            ib-button
                +icon-basic-checkmark
                |  Confirm
    </template>
</ib-example>

<ib-example id="appearance" name="Appearance">
    <template lang="pug">
        div
            ib-button(appearance="link") link
            ib-button(appearance="clear") clear
            ib-button(appearance="narrow") narrow
            ib-button(appearance="pill") pill
    </template>
</ib-example>

<ib-example id="attrs" name="Attributes">
    <template lang="pug">
        div
            ib-button(disabled) :disabled
            ib-button(inert) :inert
    </template>
</ib-example>
